import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const ExperiencePage = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          const offset = 100;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-experience';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "TouristDestination",
        "name": "Branch(브란치) 체험 장소 안내 | 한국·일본 국제교류 체험처",
        "description": "Branch(브란치)의 한국·일본 체험 장소를 소개합니다. 아모레퍼시픽, Gen.G, 시사쿡요리학원 등 50여 개 체험처와 연계된 국제교류 진로 체험 장소.",
        "url": siteUrl + "/experience",
        "inLanguage": "ko-KR",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Branch(브란치)",
          "url": siteUrl
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "홈", "item": siteUrl + "/" },
            { "@type": "ListItem", "position": 2, "name": "체험 장소 안내", "item": siteUrl + "/experience" }
          ]
        },
        "containsPlace": [
          { "@type": "TouristAttraction", "name": "아모레퍼시픽", "address": { "@type": "PostalAddress", "addressLocality": "용산구", "addressRegion": "서울특별시", "addressCountry": "KR" } },
          { "@type": "TouristAttraction", "name": "Gen.G 이스포츠", "address": { "@type": "PostalAddress", "addressLocality": "강남구", "addressRegion": "서울특별시", "addressCountry": "KR" } },
          { "@type": "TouristAttraction", "name": "시사쿡요리학원", "address": { "@type": "PostalAddress", "addressLocality": "강남구", "addressRegion": "서울특별시", "addressCountry": "KR" } }
        ]
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5b56b322fd5e77a52b42907a769818eb.jpg"
            alt="체험 장소 배경"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <span className="text-sm md:text-base tracking-[0.3em] text-white/90 font-medium uppercase mb-4 md:mb-6 block" style={{ fontFamily: 'Pretendard, sans-serif' }}>
            Experience Places
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 md:mb-8 tracking-tight" style={{ fontFamily: 'Pretendard, sans-serif' }}>
            체험 장소 안내
          </h1>
          <p className="text-base md:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>
            한국과 일본의 다양한 체험 장소를 소개합니다
          </p>
          {/* Quick Nav */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <a
              href="#korea-experience"
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 border border-white/60 text-white px-6 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm md:text-base font-semibold"
              style={{ fontFamily: 'Pretendard, sans-serif' }}
            >
              <i className="ri-map-pin-line"></i>
              한국 체험 장소
            </a>
            <a
              href="#japan-experience"
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 border border-white/60 text-white px-6 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm md:text-base font-semibold"
              style={{ fontFamily: 'Pretendard, sans-serif' }}
            >
              <i className="ri-map-pin-line"></i>
              일본 체험 장소
            </a>
          </div>
        </div>
      </section>

      {/* Korea Experience Section */}
      <section id="korea-experience" className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-14 md:mb-20">
            <span className="text-sm md:text-base tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-4 block" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              Korea Experience
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-4 tracking-tight" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              🇰🇷 한국 체험 장소
            </h2>
            <div className="w-full max-w-[240px] h-0.5 bg-[#8B7355] mx-auto mt-4 mb-6"></div>
            <p className="text-base md:text-lg text-[#5A4A3A] max-w-2xl mx-auto leading-relaxed font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              일본 학생들이 서울에서 직접 경험하는 K-문화, 산업, 직업 체험 장소입니다.
            </p>
          </div>

          {/* Category: K-Beauty */}
          <div className="mb-16 md:mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center bg-[#8B7355] rounded-full">
                <i className="ri-sparkling-line text-white text-lg"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>K-뷰티 체험</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Card 1 */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=amorepacific%20headquarters%20modern%20glass%20building%20in%20seoul%20yongsan%20district%2C%20iconic%20contemporary%20architecture%20with%20beautiful%20garden%20and%20water%20features%2C%20bright%20sunny%20day%20with%20blue%20sky%2C%20professional%20architectural%20photography%2C%20luxury%20korean%20beauty%20brand%20corporate%20campus%2C%20clean%20minimalist%20design&width=600&height=400&seq=korea-place-amore-001&orientation=landscape"
                    alt="아모레퍼시픽"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>아모레퍼시픽</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 용산구
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    K-뷰티 대표 기업 아모레퍼시픽에서 메이크업 체험 및 브랜드 스토리를 경험합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">메이크업 체험</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">브랜드 투어</span>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20korean%20beauty%20salon%20and%20cosmetics%20studio%20interior%20with%20bright%20lighting%20and%20professional%20makeup%20stations%2C%20clean%20white%20and%20rose%20gold%20aesthetic%2C%20beauty%20products%20displayed%20on%20shelves%2C%20professional%20educational%20beauty%20workshop%20setting%2C%20warm%20inviting%20atmosphere&width=600&height=400&seq=korea-place-beauty-studio-002&orientation=landscape"
                    alt="뷰티 스튜디오"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>K-뷰티 전문 스튜디오</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 강남구
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    전문 강사와 함께 한국 뷰티 트렌드를 배우고 직접 체험하는 실습 중심 프로그램입니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">스킨케어</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">메이크업 실습</span>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=vibrant%20korean%20cosmetics%20shopping%20street%20in%20myeongdong%20seoul%20with%20colorful%20store%20fronts%20and%20beauty%20product%20displays%2C%20busy%20shopping%20district%20with%20modern%20signage%2C%20bright%20daytime%20atmosphere%2C%20professional%20travel%20photography%2C%20iconic%20korean%20beauty%20shopping%20destination&width=600&height=400&seq=korea-place-myeongdong-003&orientation=landscape"
                    alt="명동 뷰티 거리"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>명동 K-뷰티 거리</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 중구 명동
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    한국 뷰티 브랜드가 밀집한 명동에서 최신 K-뷰티 트렌드를 직접 체험합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">쇼핑 체험</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">문화 탐방</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category: K-Food */}
          <div className="mb-16 md:mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center bg-[#8B7355] rounded-full">
                <i className="ri-restaurant-line text-white text-lg"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>K-푸드 & 요리 체험</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Card */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20korean%20cooking%20academy%20classroom%20with%20modern%20kitchen%20equipment%20and%20stainless%20steel%20counters%2C%20students%20in%20white%20aprons%20learning%20to%20cook%20traditional%20korean%20food%2C%20bright%20clean%20culinary%20school%20interior%2C%20professional%20educational%20photography%2C%20hands-on%20cooking%20class%20atmosphere&width=600&height=400&seq=korea-place-cooking-001&orientation=landscape"
                    alt="시사쿡요리학원"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>시사쿡요리학원</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 강남구
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    전문 셰프와 함께 한국 전통 요리를 직접 만들어보는 실습 체험 프로그램입니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">요리 실습</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">자격증 취득</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=traditional%20korean%20food%20market%20gwangjang%20market%20in%20seoul%20with%20colorful%20street%20food%20stalls%20and%20vendors%2C%20vibrant%20market%20atmosphere%20with%20bindaetteok%20and%20tteokbokki%20displays%2C%20authentic%20korean%20culinary%20culture%2C%20professional%20food%20photography%2C%20warm%20and%20lively%20market%20scene&width=600&height=400&seq=korea-place-market-002&orientation=landscape"
                    alt="광장시장"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>광장시장 K-푸드 투어</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 종로구
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    한국 전통 시장에서 다양한 길거리 음식을 맛보며 한국 식문화를 체험합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">시장 투어</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">음식 체험</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=korean%20traditional%20tea%20ceremony%20and%20hanok%20cultural%20experience%20center%20interior%2C%20beautiful%20wooden%20architecture%20with%20paper%20screens%20and%20traditional%20furniture%2C%20serene%20and%20elegant%20atmosphere%2C%20professional%20cultural%20photography%2C%20authentic%20korean%20heritage%20experience%20setting&width=600&height=400&seq=korea-place-hanok-003&orientation=landscape"
                    alt="한옥 문화 체험"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>한옥 전통 문화 체험</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 종로구 북촌
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    한옥에서 전통 다도, 한복 체험 등 한국 전통 문화를 직접 경험합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">전통 문화</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">한복 체험</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category: K-Entertainment */}
          <div className="mb-16 md:mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center bg-[#8B7355] rounded-full">
                <i className="ri-music-line text-white text-lg"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>K-엔터테인먼트 체험</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Gen%20G%20esports%20gaming%20center%20in%20seoul%20with%20high-end%20gaming%20setups%20and%20professional%20esports%20arena%2C%20modern%20gaming%20facility%20with%20LED%20lighting%20and%20multiple%20screens%2C%20professional%20esports%20environment%2C%20clean%20and%20impressive%20gaming%20venue%20interior%2C%20career%20exploration%20in%20gaming%20industry&width=600&height=400&seq=korea-place-geng-001&orientation=landscape"
                    alt="Gen.G"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>Gen.G 이스포츠</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 강남구
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    글로벌 이스포츠 기업 Gen.G에서 게임 산업과 직업 세계를 탐색합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">이스포츠 체험</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">직업 탐색</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20kpop%20dance%20academy%20studio%20in%20seoul%20with%20mirrored%20walls%20and%20professional%20dance%20floor%2C%20students%20learning%20kpop%20choreography%20with%20instructor%2C%20bright%20studio%20lighting%2C%20energetic%20dance%20practice%20atmosphere%2C%20professional%20dance%20education%20photography&width=600&height=400&seq=korea-place-dance-002&orientation=landscape"
                    alt="모어댄스뮤직아카데미"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>모어댄스뮤직아카데미</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 마포구
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    K-팝 댄스와 음악을 전문 강사에게 배우는 체험 프로그램입니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">K-팝 댄스</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">음악 체험</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=NH%20nonghyup%20bank%20modern%20branch%20interior%20with%20professional%20banking%20environment%20and%20financial%20education%20displays%2C%20clean%20contemporary%20bank%20design%20with%20warm%20lighting%2C%20career%20exploration%20in%20finance%20industry%2C%20professional%20corporate%20photography&width=600&height=400&seq=korea-place-bank-003&orientation=landscape"
                    alt="NH농협은행"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>NH농협은행</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>서울 중구
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    금융 산업의 현장을 직접 방문하여 금융 직업 세계를 탐색합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">금융 체험</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">직업 탐색</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* More Info Banner */}
          <div className="bg-[#F5EFE7] rounded-2xl p-8 md:p-12 text-center">
            <p className="text-base md:text-lg text-[#5A4A3A] font-medium mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              위 장소 외에도 <strong className="text-[#8B7355]">50여 개 이상</strong>의 체험처와 연계되어 있습니다.
            </p>
            <p className="text-sm md:text-base text-[#8B7355] font-medium mb-6" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              원하시는 체험 장소가 있다면 문의해 주세요!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <Link
                to="/apply"
                className="inline-flex items-center gap-2 bg-[#8B7355] hover:bg-[#6d5a42] text-white px-7 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer font-semibold text-sm md:text-base"
                style={{ fontFamily: 'Pretendard, sans-serif' }}
              >
                프로그램 신청하기
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="https://brancheduinfo.readdy.co"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#2C2416] border-2 border-[#2C2416] px-7 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer font-semibold text-sm md:text-base"
                style={{ fontFamily: 'Pretendard, sans-serif' }}
              >
                더 많은 체험장소 보기
                <i className="ri-map-pin-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-[#E8DFD0]"></div>
      </div>

      {/* Japan Experience Section */}
      <section id="japan-experience" className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-14 md:mb-20">
            <span className="text-sm md:text-base tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-4 block" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              Japan Experience
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-4 tracking-tight" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              🇯🇵 일본 체험 장소
            </h2>
            <div className="w-full max-w-[240px] h-0.5 bg-[#8B7355] mx-auto mt-4 mb-6"></div>
            <p className="text-base md:text-lg text-[#5A4A3A] max-w-2xl mx-auto leading-relaxed font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              한국 학생들이 일본에서 직접 경험하는 첨단 산업, 전통 문화, 학교 교류 체험 장소입니다.
            </p>
          </div>

          {/* Category: Tech & Industry */}
          <div className="mb-16 md:mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center bg-[#8B7355] rounded-full">
                <i className="ri-robot-line text-white text-lg"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>첨단 기술 & 산업 체험</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=japanese%20robotics%20research%20laboratory%20with%20advanced%20humanoid%20robots%20and%20AI%20technology%20displays%2C%20modern%20clean%20white%20facility%20with%20high-tech%20equipment%20and%20robotic%20arms%2C%20professional%20industrial%20photography%2C%20inspiring%20technology%20and%20innovation%20atmosphere%2C%20bright%20clinical%20lighting&width=600&height=400&seq=japan-place-robot-001&orientation=landscape"
                    alt="로봇·AI 기업"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>일본 로봇·AI 기업 견학</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>도쿄 / 오사카
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    세계 최고 수준의 일본 로봇·AI 기업을 방문하여 첨단 기술 현장을 직접 체험합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">로봇 체험</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">AI 기술</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20japanese%20university%20campus%20with%20contemporary%20architecture%20and%20research%20facilities%2C%20students%20walking%20through%20academic%20buildings%20with%20professor%2C%20bright%20sunny%20day%20with%20cherry%20blossom%20trees%2C%20professional%20educational%20photography%2C%20prestigious%20japanese%20university%20atmosphere&width=600&height=400&seq=japan-place-univ-002&orientation=landscape"
                    alt="일본 대학 방문"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>일본 대학 및 연구소 방문</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>도쿄 / 교토
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    일본 명문 대학 캠퍼스와 연구소를 방문하여 학문적 영감을 얻고 진로를 탐색합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">대학 탐방</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">연구소 견학</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=japanese%20high%20school%20classroom%20with%20students%20in%20uniforms%20sitting%20at%20desks%20during%20lesson%2C%20bright%20modern%20classroom%20with%20natural%20window%20light%20and%20educational%20materials%20on%20walls%2C%20authentic%20japanese%20school%20environment%2C%20professional%20documentary%20photography%2C%20cultural%20exchange%20and%20educational%20atmosphere&width=600&height=400&seq=japan-place-school-003&orientation=landscape"
                    alt="일본 학교 수업 교류"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>일본 학교 수업 교류</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>도쿄 / 오사카 / 교토
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    일본 현지 학교를 방문하여 수업에 참관하고 일본 학생들과 교류합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">수업 참관</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">학생 교류</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category: Traditional Culture */}
          <div className="mb-16 md:mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center bg-[#8B7355] rounded-full">
                <i className="ri-ancient-pavilion-line text-white text-lg"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>전통 문화 & 장인 체험</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=japanese%20master%20craftsman%20demonstrating%20traditional%20woodworking%20and%20lacquerware%20techniques%20in%20authentic%20workshop%2C%20artisan%20working%20with%20traditional%20tools%20in%20warm%20wooden%20studio%2C%20professional%20documentary%20photography%2C%20cultural%20heritage%20preservation%20and%20skill%20learning%2C%20warm%20natural%20lighting%20with%20sawdust%20and%20craftsmanship%20atmosphere&width=600&height=400&seq=japan-place-craft-001&orientation=landscape"
                    alt="장인 기술 체험"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>일본 장인 기술 체험</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>교토 / 나라
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    수백 년 전통을 이어온 일본 장인에게 직접 전통 기술을 배우는 특별한 체험입니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">장인 체험</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">전통 공예</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=beautiful%20japanese%20temple%20and%20shrine%20complex%20in%20kyoto%20with%20traditional%20architecture%20and%20stone%20lanterns%2C%20serene%20garden%20with%20maple%20trees%20and%20stone%20pathways%2C%20professional%20travel%20photography%2C%20iconic%20japanese%20cultural%20heritage%20site%2C%20warm%20autumn%20colors%20and%20peaceful%20atmosphere&width=600&height=400&seq=japan-place-temple-002&orientation=landscape"
                    alt="교토 문화 탐방"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>교토 전통 문화 탐방</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>교토
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    일본 전통 문화의 중심지 교토에서 신사, 사찰, 전통 거리를 탐방합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">문화 탐방</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">역사 체험</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8DFD0] hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=japanese%20tea%20ceremony%20experience%20in%20traditional%20tatami%20room%20with%20matcha%20preparation%2C%20serene%20and%20elegant%20setting%20with%20bamboo%20garden%20view%20through%20shoji%20screens%2C%20professional%20cultural%20photography%2C%20authentic%20japanese%20hospitality%20and%20mindfulness%20experience%2C%20soft%20natural%20lighting&width=600&height=400&seq=japan-place-tea-003&orientation=landscape"
                    alt="다도 체험"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>일본 다도 & 전통 체험</h4>
                  <p className="text-sm text-[#8B7355] font-medium mb-3" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    <i className="ri-map-pin-2-line mr-1"></i>교토 / 도쿄
                  </p>
                  <p className="text-sm text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    일본 전통 다도와 기모노 체험을 통해 일본 문화의 정수를 경험합니다.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">다도 체험</span>
                    <span className="text-xs bg-[#F5EFE7] text-[#8B7355] px-3 py-1 rounded-full font-medium">기모노 체험</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* More Info Banner */}
          <div className="bg-[#F5EFE7] rounded-2xl p-8 md:p-12 text-center">
            <p className="text-base md:text-lg text-[#5A4A3A] font-medium mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              일본 체험 장소는 <strong className="text-[#8B7355]">운영 방식 및 일정에 따라 맞춤 구성</strong>됩니다.
            </p>
            <p className="text-sm md:text-base text-[#8B7355] font-medium mb-6" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              항공·비자·숙박 협조가 필요한 경우 별도 안내해 드립니다.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <Link
                to="/apply"
                className="inline-flex items-center gap-2 bg-[#8B7355] hover:bg-[#6d5a42] text-white px-7 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer font-semibold text-sm md:text-base"
                style={{ fontFamily: 'Pretendard, sans-serif' }}
              >
                프로그램 신청하기
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="https://brancheduinfo.readdy.co"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#2C2416] border-2 border-[#2C2416] px-7 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer font-semibold text-sm md:text-base"
                style={{ fontFamily: 'Pretendard, sans-serif' }}
              >
                더 많은 체험장소 보기
                <i className="ri-map-pin-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExperiencePage;
